import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tag, 
  Button, 
  Avatar, 
  Divider, 
  Spin, 
  message,
  Tooltip,
  Empty,
  Input,
  List,
  Space
} from 'antd';
import { 
  CalendarOutlined, 
  EyeOutlined, 
  UserOutlined,
  LikeOutlined,
  CommentOutlined,
  ShareAltOutlined,
  EditOutlined,
  DeleteOutlined,
  LikeFilled,
  MessageOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams, useNavigate } from 'react-router-dom';
import { getPostById, addComment, likePost } from '../../api/mockData';
import './index.css';

const { TextArea } = Input;

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [userLiked, setUserLiked] = useState(false);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const data = await getPostById(id);
      setPost(data.post);
      setComments(data.comments || []);
      setUserLiked(data.userLiked || false);
    } catch (error) {
      console.error('获取文章失败:', error);
      message.error('获取文章失败');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    try {
      await likePost(id);
      setPost(prev => ({
        ...prev,
        likes: userLiked ? prev.likes - 1 : prev.likes + 1
      }));
      setUserLiked(!userLiked);
      message.success(userLiked ? '已取消点赞' : '点赞成功');
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      message.warning('请输入评论内容');
      return;
    }

    setSubmittingComment(true);
    try {
      const newComment = {
        id: Date.now().toString(),
        author: '当前用户',
        avatar: null,
        content: commentText,
        createdAt: new Date().toISOString(),
        likes: 0
      };
      
      // 模拟API调用
      await addComment(id, commentText);
      
      setComments(prev => [newComment, ...prev]);
      setCommentText('');
      message.success('评论发表成功');
    } catch (error) {
      message.error('评论发表失败');
    } finally {
      setSubmittingComment(false);
    }
  };

  // 格式化日期：YYYY年MM月DD日 HH:mm
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}年${month}月${day}日 ${hours}:${minutes}`;
  };

  // 相对时间（刚刚，X分钟前，X小时前，X天前）
  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);
    
    if (diffInYears > 0) {
      return `${diffInYears}年前`;
    }
    if (diffInMonths > 0) {
      return `${diffInMonths}个月前`;
    }
    if (diffInDays > 0) {
      return `${diffInDays}天前`;
    }
    if (diffInHours > 0) {
      return `${diffInHours}小时前`;
    }
    if (diffInMinutes > 0) {
      return `${diffInMinutes}分钟前`;
    }
    return '刚刚';
  };

  // 格式化日期为简单格式：MM月DD日 HH:mm
  const formatSimpleDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}月${day}日 ${hours}:${minutes}`;
  };

  if (loading) {
    return (
      <div className="post-detail-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!post) {
    return (
      <Empty
        description="文章不存在"
        className="post-detail-empty"
      >
        <Button type="primary" onClick={() => navigate('/')}>
          返回首页
        </Button>
      </Empty>
    );
  }

  return (
    <div className="post-detail-container">
      <Card className="post-detail-card">
        <div className="post-header">
          <div className="post-meta">
            <span className="post-date">
              <CalendarOutlined /> {formatDate(post.createdAt)}
            </span>
            <span className="post-views">
              <EyeOutlined /> {post.views || 0}
            </span>
          </div>
          
          <h1 className="post-title">{post.title}</h1>
          
          <div className="post-author">
            <Avatar 
              size="large" 
              src={post.authorAvatar} 
              icon={<UserOutlined />}
            />
            <div className="author-info">
              <span className="author-name">{post.author}</span>
              <span className="post-read-time">阅读时间: {post.readTime || '5分钟'}</span>
            </div>
          </div>
          
          <div className="post-tags">
            {post.tags?.map(tag => (
              <Tag key={tag} color="blue">{tag}</Tag>
            ))}
          </div>
        </div>

        <Divider />

        <div className="post-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 className="markdown-h1" {...props} />,
              h2: ({node, ...props}) => <h2 className="markdown-h2" {...props} />,
              h3: ({node, ...props}) => <h3 className="markdown-h3" {...props} />,
              p: ({node, ...props}) => <p className="markdown-p" {...props} />,
              code: ({node, inline, ...props}) => 
                inline ? 
                  <code className="markdown-inline-code" {...props} /> : 
                  <code className="markdown-code-block" {...props} />,
              blockquote: ({node, ...props}) => 
                <blockquote className="markdown-blockquote" {...props} />,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>

        <Divider />

        <div className="post-actions">
          <Button
            type="text"
            icon={userLiked ? <LikeFilled style={{ color: '#eb2f96' }} /> : <LikeOutlined />}
            onClick={handleLike}
            className="post-action-btn"
          >
            {post.likes || 0}
          </Button>
          
          <Button
            type="text"
            icon={<CommentOutlined />}
            className="post-action-btn"
          >
            {comments.length}
          </Button>
          
          <Button
            type="text"
            icon={<ShareAltOutlined />}
            className="post-action-btn"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              message.success('链接已复制到剪贴板');
            }}
          >
            分享
          </Button>
          
          {post.canEdit && (
            <>
              <Button
                type="text"
                icon={<EditOutlined />}
                className="post-action-btn"
                onClick={() => navigate(`/edit/${id}`)}
              >
                编辑
              </Button>
              
              <Button
                type="text"
                icon={<DeleteOutlined />}
                danger
                className="post-action-btn"
              >
                删除
              </Button>
            </>
          )}
        </div>
      </Card>

      <Card 
        title={
          <Space>
            <MessageOutlined />
            <span>评论 ({comments.length})</span>
          </Space>
        } 
        className="comments-card"
      >
        <div className="comment-editor">
          <TextArea
            rows={4}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="写下你的评论..."
            className="comment-input"
            maxLength={500}
            showCount
          />
          <div className="comment-editor-actions">
            <Button
              type="primary"
              loading={submittingComment}
              onClick={handleAddComment}
              className="comment-submit-btn"
              disabled={!commentText.trim()}
            >
              发表评论
            </Button>
          </div>
        </div>

        <div className="comments-list">
          {comments.length === 0 ? (
            <Empty
              description="暂无评论，快来发表第一条评论吧！"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              className="comments-empty"
            />
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={comments}
              renderItem={(comment) => (
                <List.Item className="comment-item">
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        src={comment.avatar} 
                        icon={<UserOutlined />}
                      />
                    }
                    title={
                      <div className="comment-header">
                        <span className="comment-author">{comment.author}</span>
                        <span className="comment-time">
                          <Tooltip title={formatDate(comment.createdAt)}>
                            {getRelativeTime(comment.createdAt)}
                          </Tooltip>
                        </span>
                      </div>
                    }
                    description={
                      <div className="comment-content">
                        <p>{comment.content}</p>
                        <div className="comment-actions">
                          <Button type="text" size="small" icon={<LikeOutlined />}>
                            {comment.likes || 0}
                          </Button>
                          <Button type="text" size="small">
                            回复
                          </Button>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>
      </Card>
    </div>
  );
};

export default PostDetail;